import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useNavigate } from 'react-router-dom';
import type { NotificationFilterKey } from './notificationFormatters';
import { CWY_COLORS } from '../../theme/colors';

interface NotificationEmptyStateProps {
  filter: NotificationFilterKey;
}

const FILTER_HINTS: Record<NotificationFilterKey, { title: string; subtitle: string; cta?: { label: string; path: string } }> = {
  all: {
    title: "You're all caught up",
    subtitle: 'When you get messages, community updates, or booking confirmations, they’ll show up here.',
  },
  messaging: {
    title: 'No message notifications',
    subtitle: 'Start a conversation with CWY Support or check your communities.',
    cta: { label: 'Open Messaging', path: '/chat' },
  },
  community: {
    title: 'No community updates',
    subtitle: 'Join a community to see member activity and announcements.',
    cta: { label: 'Browse Communities', path: '/chat' },
  },
  appointment: {
    title: 'No appointment updates',
    subtitle: 'Book a consultation and confirmations will appear here.',
    cta: { label: 'Book appointment', path: '/appointments' },
  },
  store: {
    title: 'No store notifications',
    subtitle: 'Purchases and order updates will show up in this tab.',
    cta: { label: 'Visit Store', path: '/store' },
  },
  vault: {
    title: 'No wallet notifications',
    subtitle: 'Deposits, transfers, and grant updates will appear here.',
    cta: { label: 'Open Wallet', path: '/profile/wallet' },
  },
};

export const NotificationEmptyState: React.FC<NotificationEmptyStateProps> = ({ filter }) => {
  const navigate = useNavigate();
  const content = FILTER_HINTS[filter];

  return (
    <Box
      sx={{
        px: 3,
        py: 6,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: 'var(--cwy-unreadBg, rgba(0, 113, 133, 0.08))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        <NotificationsNoneOutlinedIcon sx={{ fontSize: 36, color: CWY_COLORS.teal }} />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: CWY_COLORS.textPrimary }}>
        {content.title}
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: CWY_COLORS.textSecondary, maxWidth: 360, lineHeight: 1.55, fontSize: '0.9375rem' }}
      >
        {content.subtitle}
      </Typography>

      {content.cta && (
        <Button
          variant="outlined"
          onClick={() => navigate(content.cta!.path)}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '20px',
            px: 2.5,
            borderColor: CWY_COLORS.border,
            color: CWY_COLORS.textPrimary,
            '&:hover': { borderColor: CWY_COLORS.teal, bgcolor: 'var(--cwy-unreadBg, rgba(0, 113, 133, 0.08))' },
          }}
        >
          {content.cta.label}
        </Button>
      )}
    </Box>
  );
};
